import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  signal,
  computed,
  Injectable,
  Input,
  Output,
  EventEmitter,
  effect,
  input,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, takeUntil, catchError, finalize, of } from 'rxjs';

// ─── PrimeNG Imports (PrimeNG 18 standalone) ────────────────────────────────
import { RfpService } from '../../views/layout/rfpcontract/service/rfpservice.service';
import { AppToastComponent } from '../toast/toast.component';
import { AppSkeletonComponent } from '../app-skeleton/app-skeleton.component';
import { AppConfirmationService } from '../../services/confirmation.service';
import { AppConfirmDialogComponent } from '../app-confirm-dialog/app-confirm-dialog.component';
import { AppMessageService } from '../../services/Messageservice';
import { CommonModule } from '@angular/common';

/**document list */

export interface RfpDocument {
  id: number;
  client_id: number;
  kb_doc_ids: string[] | null;
  doc_name?: string;
  doc_size?: string;
  doc_type?: string;
  created_at?: string;
  created_by?: string;
  created_by_id?: number;
  updated_at?: string | null;
  status?: string;
  status_id?: number;
  is_expired?: boolean;
  is_head_version?: boolean;
  version_no?: number;
  ai_msg?: string | null;
  ai_time_taken?: string | null;
  parent_rfp_document_id?: string | null;
  metadata?: Metadata;
  sheet_details?: SheetDetails | SheetDetails[] | null;
  termination_date?: string | null;
  title?: string;
}

export interface Metadata {
  assign_to?: string;
  assign_to_name?: string;
  company_name?: string;
  company_relationship?: string;
  contract_value?: string;
  effective_date?: string;
  rfp_type_id?: string;
  title?: string;
  auto_detected?: string;
  chunks_created?: string;
  doc_type_detected?: string;
  document_id?: string;
  excel_config_used?: string;
  summary?: string;
}

export interface SheetDetails {
  file_name?: string;
  sheets?: Sheet[];
}

export interface Sheet {
  sheet_name?: string;
  question_field?: string;
  answer_field?: string;
  comments_field?: string;
  attachments_field?: string;
  starting_row?: number;
}

export interface Pagination {
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
}

export interface QAInputItem {
  question: string;
  answer: string;
}

export interface QAPair {
  id: string;
  questionNumber: number;
  question: string;
  answer: string;
  originalAnswer: string;
  aiExtractedAnswer?: string;
  isLoadingAiExtract: boolean;
  isAiAnswerActive: boolean;
  isDirty: boolean;
  confidence?: number;
}

export interface QASavePayload {
  client_id: number;
  doc_id: number;
  data: Array<{
    question_no: number;
    question: string;
    answer: string;
  }>;
}

export interface AiExtractRequest {
  question: string[];
  doc_id: number;
  client_id: number;
  kb_doc_ids: string;
}

export interface AiExtractResultItem {
  answer: string;
  question: string;
}

export interface AiExtractResponse {
  message: string;
  result: AiExtractResultItem[];
}

// ─── AI Extraction Service ────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class QADataService {
  private readonly http = inject(HttpClient);

  extractAnswerWithAI(
    questionText: string,
    docId: number,
    clientId: number,
    kbDocIds: string,
  ) {
    const payload: AiExtractRequest = {
      question: [questionText],
      doc_id: docId,
      client_id: clientId,
      kb_doc_ids: kbDocIds,
    };

    return this.http
      .post<AiExtractResponse>('/api/ai/extract-answer', payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(catchError(() => of(MOCK_AI_FALLBACK_RESPONSE(questionText))));
  }
}

const MOCK_AI_FALLBACK_RESPONSE = (
  questionText: string,
): AiExtractResponse => ({
  message: 'Answer extracted!',
  result: [
    {
      question: questionText,
      answer:
        '[Fallback] Could not reach the extraction service. Please retry.',
    },
  ],
});

const toId = (index: number): string => `q${index + 1}`;

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'app-qa-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AppConfirmationService],
  imports: [
    FormsModule,CommonModule,
    AppConfirmDialogComponent,
    AppToastComponent,
    AppSkeletonComponent,
  ],
  template: `
    <app-toast position="top-right" [life]="4000" />
    <app-confirm-dialog />

    <div class="qa-renderer-container">
      <!-- ── PAGE HEADER ──────────────────────────────────────────────── -->
      <header class="qa-header">
        <div class="qa-header__inner">
          <!-- Left: breadcrumb + title + stats -->
          <div class="qa-header__meta">
            <div class="qa-header__breadcrumb">
              <i class="pi pi-home"></i>
              <span class="qa-breadcrumb-sep">/</span>
              <span>Question Repository</span>
              <span class="qa-breadcrumb-sep">/</span>
              <span class="qa-breadcrumb-active">Document Analysis</span>
            </div>
            <h2 class="qa-header__title">
              {{ documentTitle || 'Document Analysis' }}
            </h2>
            <div class="qa-header__stats">
              <span class="qa-stat-badge">
                <i class="pi pi-list"></i>
                {{ qaPairs().length }} Questions
              </span>
              @if (answeredByAI() > 0) {
                <span class="qa-stat-badge qa-stat-badge--ai">
                  <i class="pi pi-sparkles"></i>
                  {{ answeredByAI() }} AI Extracted
                </span>
              }
              @if (dirtyCount() > 0) {
                <span class="qa-stat-badge qa-stat-badge--warn">
                  <i class="pi pi-pencil"></i>
                  {{ dirtyCount() }} Unsaved
                </span>
              }
            </div>
          </div>

          <!-- Right: Save + Submit -->
          @if (documentStatus.toLowerCase() === 'created') {
            <div class="qa-header__actions">
              <button
                type="button"
                [disabled]="dirtyCount() === 0 || isSubmitted()"
                (click)="onSave()"
                class="qa-btn qa-btn--outline"
                title="Save current answers"
              >
                Save
              </button>

              <button
                type="button"
                (click)="onSubmit()"
                [disabled]="dirtyCount() > 0 || isSubmitted()"
                class="qa-btn qa-btn--primary"
                title="Submit all answers for review"
              >
                Submit
              </button>
            </div>
          }
        </div>
      </header>

      <!-- ── QA PAIRS LIST ────────────────────────────────────────────── -->
      @if (qaPairs().length > 0) {
        <main class="qa-list">
          @for (pair of qaPairs(); track trackById($index, pair)) {
            <article
              class="qa-card"
              [class.qa-card--ai-active]="pair.isAiAnswerActive"
              [class.qa-card--dirty]="pair.isDirty && !pair.isAiAnswerActive"
            >
              <!-- ── Card Top Row ── -->
              <div class="qa-card__top">
                <div class="qa-card__header-content">
                  <div class="qa-card__badges">
                    @if (pair.isAiAnswerActive) {
                      <span class="qa-badge qa-badge--ai">
                        <i class="pi pi-sparkles"></i> AI Enhanced
                      </span>
                    }
                    @if (pair.isDirty && !pair.isAiAnswerActive) {
                      <span class="qa-badge qa-badge--edited">
                        <i class="pi pi-pencil"></i> Edited
                      </span>
                    }
                  </div>
                  <!-- ── Per-card actions ── -->
                  @if (documentStatus.toLowerCase() === 'created') {
                    <div class="qa-card__actions">
                      <button
                        type="button"
                        [disabled]="pair.isLoadingAiExtract || isSubmitted()"
                        (click)="triggerAiExtract(pair)"
                        class="qa-btn qa-btn--primary qa-btn--sm"
                        title="Use AI to extract a more precise answer"
                      >
                        <i
                          *ngIf="pair.isLoadingAiExtract"
                          class="pi pi-spin pi-spinner"
                        ></i>
                        {{
                          pair.isLoadingAiExtract
                            ? 'Extracting…'
                            : 'Extract Answer'
                        }}
                      </button>
                      @if (pair.isDirty) {
                        <button
                          type="button"
                          label="Reset"
                          icon="pi pi-undo"
                          [disabled]="pair.isLoadingAiExtract"
                          (click)="resetToOriginal(pair)"
                          class="qa-btn qa-btn--ghost qa-btn--sm"
                          pTooltip="Restore original answer"
                          tooltipPosition="left"
                        ></button>
                      }
                    </div>
                  }
                </div>
              </div>
              <!-- ── Question block ── -->
              <div class="qa-card__question-block">
                <p class="qa-card__question-text">{{ pair.question }}</p>
              </div>
              <div class="qa-card__divider"></div>
              <!-- ── Answer block ── -->
              <div
                class="qa-card__answer-block"
                [class.qa-card__answer-block--ai]="pair.isAiAnswerActive"
              >
                <div
                  class="qa-section-label"
                  [class.qa-section-label--ai]="pair.isAiAnswerActive"
                >
                  <i
                    [class]="
                      pair.isAiAnswerActive
                        ? 'pi pi-sparkles'
                        : 'pi pi-check-circle'
                    "
                  ></i>
                  <span>{{
                    pair.isAiAnswerActive ? 'AI Extracted Answer' : 'Answer'
                  }}</span>
                </div>
                <!-- Shimmer while AI is running -->
                @if (pair.isLoadingAiExtract) {
                  <div class="qa-answer-skeleton">
                    <app-skeleton
                      width="100%"
                      height="14px"
                      borderRadius="4px"
                    />
                    <app-skeleton
                      width="88%"
                      height="14px"
                      borderRadius="4px"
                      styleClass="mt-2"
                    />
                    <app-skeleton
                      width="94%"
                      height="14px"
                      borderRadius="4px"
                      styleClass="mt-2"
                    />
                    <app-skeleton
                      width="55%"
                      height="14px"
                      borderRadius="4px"
                      styleClass="mt-2"
                    />
                  </div>
                }
                <!-- Editable textarea -->
                @if (!pair.isLoadingAiExtract) {
                  <div class="qa-answer-wrapper">
                    <textarea
                      [(ngModel)]="pair.answer"
                      (ngModelChange)="onAnswerEdit(pair)"
                      [rows]="4"
                      [readonly]="isReadOnly"
                      class="qa-answer-textarea"
                      [class.qa-answer-textarea--ai]="pair.isAiAnswerActive"
                      [attr.aria-label]="
                        'Answer for question ' + pair.questionNumber
                      "
                    ></textarea>
                    <!--  [class.qa-answer-textarea--editable]="isAnswerEditable(pair)"  -->
                    @if (pair.isAiAnswerActive) {
                      <div class="qa-answer-footer">
                        <i class="pi pi-info-circle"></i>
                        <span
                          >Answer extracted by AI from your knowledge base</span
                        >
                      </div>
                    }
                  </div>
                }
              </div>
            </article>
          }
        </main>
      }

      <!-- ── EMPTY STATE ─────────────────────────────────────────────── -->
      @if (qaPairs().length === 0) {
        <div class="qa-empty">
          <div class="qa-empty__icon-wrap">
            <i class="pi pi-inbox"></i>
          </div>
          <h3 class="qa-empty__title">No Questions Found</h3>
          <p class="qa-empty__desc">
            There are currently no questions to display.
          </p>
        </div>
      }
    </div>
  `,
  styles: [
    `
      /* ── Design tokens — aligned to Mindpro Information Security app ── */
      :host {
        /* Core palette from screenshot */
        --app-primary: #1976d1; /* deep blue — nav/action buttons */
        --app-primary-hover: #1976d2;
        --app-primary-light: #e3f2fd; /* very light blue tint */
        --app-primary-mid: #42a5f5; /* medium blue accent */

        --app-surface: #ffffff;
        --app-bg: #f5f7fa; /* page background */
        --app-bg-row: #fafafa;

        --app-border: #e0e4ea;
        --app-border-focus: #1976d2;

        --app-text-primary: #212529;
        --app-text-secondary: #546e7a;
        --app-text-muted: #90a4ae;
        --app-text-onblue: #ffffff;

        --app-success: #2e7d32;
        --app-success-bg: #e8f5e9;
        --app-warn: #e65100;
        --app-warn-bg: #fff3e0;
        --app-ai-color: #1565c0;
        --app-ai-bg: #e3f2fd;

        --app-header-bg: #1a73e8; /* top bar blue from screenshot */
        --app-table-header: #37474f; /* dark slate table header */

        --app-radius: 6px;
        --app-radius-sm: 4px;
        --app-radius-pill: 100px;

        --app-shadow-card:
          0 1px 4px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
        --app-shadow-hover:
          0 2px 8px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06);

        /* Typography — matching the clean sans-serif in the screenshot */
        --app-font:
          'Segoe UI', 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
        --app-font-size-xs: 0.72rem;
        --app-font-size-sm: 0.8125rem;
        --app-font-size-base: 0.875rem;
        --app-font-size-md: 0.9375rem;
        --app-font-size-lg: 1.0625rem;
        --app-font-size-xl: 1.25rem;

        font-family: var(--app-font);
        font-size: var(--app-font-size-base);
        color: var(--app-text-primary);
        display: block;
      }

      /* ── Page Wrapper ────────────────────────────────────────────────── */
      .qa-renderer-container {
        min-height: 100vh;
        background: var(--app-bg);
        padding-bottom: 3rem;
      }

      /* ── Page Header — white bar below the nav ───────────────────────── */
      .qa-header {
        background: var(--app-surface);
        border-bottom: 1px solid var(--app-border);
        padding: 1rem 0;
        margin-bottom: 1.5rem;
      }

      .qa-header__inner {
        max-width: 1100px;
        margin: 0 auto;
        padding: 0 1.5rem;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
      }

      /* Breadcrumb */
      .qa-header__breadcrumb {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        font-size: var(--app-font-size-xs);
        color: var(--app-text-muted);
        margin-bottom: 0.5rem;
        font-weight: 500;
      }

      .qa-header__breadcrumb .pi-home {
        font-size: 0.75rem;
        color: var(--app-text-muted);
      }

      .qa-breadcrumb-sep {
        color: var(--app-border);
        font-size: 0.65rem;
      }

      .qa-breadcrumb-active {
        color: var(--app-primary);
        font-weight: 600;
      }

      /* Title */
      .qa-header__title {
        font-size: var(--app-font-size-xl);
        font-weight: 700;
        color: var(--app-text-primary);
        margin: 0 0 0.6rem;
        letter-spacing: -0.01em;
        line-height: 1.3;
      }

      /* Stats row */
      .qa-header__stats {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        align-items: center;
      }

      .qa-stat-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        font-size: var(--app-font-size-xs);
        font-weight: 600;
        padding: 0.25rem 0.65rem;
        border-radius: var(--app-radius-pill);
        background: var(--app-bg);
        border: 1px solid var(--app-border);
        color: var(--app-text-secondary);
        line-height: 1;
      }

      .qa-stat-badge .pi {
        font-size: 0.65rem;
      }

      .qa-stat-badge--ai {
        background: var(--app-ai-bg);
        border-color: rgba(21, 101, 192, 0.2);
        color: var(--app-ai-color);
      }

      .qa-stat-badge--warn {
        background: var(--app-warn-bg);
        border-color: rgba(230, 81, 0, 0.2);
        color: var(--app-warn);
      }

      /* Actions */
      .qa-header__actions {
        display: flex;
        gap: 0.6rem;
        align-items: center;
        flex-shrink: 0;
      }

      /* ── Shared Button System ────────────────────────────────────────── */
      :host ::ng-deep .qa-btn {
        font-family: var(--app-font) !important;
        font-size: var(--app-font-size-sm) !important;
        font-weight: 600 !important;
        border-radius: var(--app-radius) !important;
        padding: 0.5rem 1.1rem !important;
        transition:
          background 0.15s,
          border-color 0.15s,
          box-shadow 0.15s,
          transform 0.12s !important;
        letter-spacing: 0.01em !important;
        white-space: nowrap;
        line-height: 1.4 !important;
      }

      /* Primary — filled blue, matches action buttons in screenshot */
      :host ::ng-deep .qa-btn--primary {
        background: var(--app-primary) !important;
        border: 1px solid var(--app-primary) !important;
        color: var(--app-text-onblue) !important;
        box-shadow: 0 1px 3px rgba(21, 101, 192, 0.25) !important;
      }

      :host ::ng-deep .qa-btn--primary:not([disabled]):hover {
        background: var(--app-primary-hover) !important;
        border-color: var(--app-primary-hover) !important;
        box-shadow: 0 2px 8px rgba(21, 101, 192, 0.35) !important;
        transform: translateY(-1px);
      }

      /* Outline — white bg with border */
      :host ::ng-deep .qa-btn--outline {
        background: var(--app-surface) !important;
        border: 1px solid var(--app-border) !important;
        color: var(--app-text-primary) !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06) !important;
      }

      :host ::ng-deep .qa-btn--outline:not([disabled]):hover {
        border-color: var(--app-primary) !important;
        color: var(--app-primary) !important;
        background: var(--app-primary-light) !important;
      }

      /* Ghost — text-only, no border */
      :host ::ng-deep .qa-btn--ghost {
        background: transparent !important;
        border: 1px solid transparent !important;
        color: var(--app-text-secondary) !important;
        box-shadow: none !important;
      }

      :host ::ng-deep .qa-btn--ghost:not([disabled]):hover {
        background: var(--app-bg) !important;
        border-color: var(--app-border) !important;
        color: var(--app-text-primary) !important;
      }

      /* Small variant */
      :host ::ng-deep .qa-btn--sm {
        font-size: var(--app-font-size-xs) !important;
        padding: 0.35rem 0.75rem !important;
      }

      :host ::ng-deep .qa-btn[disabled] {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
        transform: none !important;
      }

      /* ── QA List ─────────────────────────────────────────────────────── */
      .qa-list {
        max-width: 1100px;
        margin: 0 auto;
        padding: 0 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      /* ── QA Card — matches the table row style in screenshot ─────────── */
      .qa-card {
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        padding: 1.25rem 1.5rem;
        box-shadow: var(--app-shadow-card);
        transition:
          box-shadow 0.2s ease,
          border-color 0.2s ease;
        position: relative;
        overflow: hidden;
      }

      /* Left accent bar */
      .qa-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 3px;
        height: 100%;
        background: var(--app-border);
        border-radius: 0 0 0 var(--app-radius);
        transition: background 0.25s;
      }

      .qa-card:hover {
        box-shadow: var(--app-shadow-hover);
        border-color: #c8d4e0;
      }

      /* AI active state */
      .qa-card--ai-active {
        border-color: rgba(21, 101, 192, 0.3);
      }

      .qa-card--ai-active::before {
        background: var(--app-primary);
      }

      /* Dirty/edited state */
      .qa-card--dirty {
        border-color: rgba(230, 81, 0, 0.25);
      }

      .qa-card--dirty::before {
        background: var(--app-warn);
      }

      /* ── Card Top Row ──────────────────────────────────────────────── */
      .qa-card__top {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        margin-bottom: 1rem;
      }

      /* Number badge — matches the green "2" version badge in screenshot */
      .qa-card__index-badge {
        width: 32px;
        height: 32px;
        min-width: 32px;
        border-radius: var(--app-radius-sm);
        background: var(--app-primary-light);
        color: var(--app-primary);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--app-font-size-sm);
        font-weight: 700;
        transition:
          background 0.25s,
          color 0.25s;
        flex-shrink: 0;
      }

      .qa-card__index-badge--ai {
        background: var(--app-primary);
        color: #fff;
      }

      .qa-card__header-content {
        flex: 1;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.6rem;
        min-height: 32px;
      }

      .qa-card__badges {
        display: flex;
        gap: 0.4rem;
        align-items: center;
        flex-wrap: wrap;
      }

      /* Inline badge — matches status chips in screenshot */
      .qa-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        font-size: var(--app-font-size-xs);
        font-weight: 600;
        padding: 0.2rem 0.55rem;
        border-radius: var(--app-radius-pill);
        line-height: 1.6;
      }

      .qa-badge .pi {
        font-size: 0.65rem;
      }

      .qa-badge--ai {
        background: var(--app-ai-bg);
        color: var(--app-ai-color);
        border: 1px solid rgba(21, 101, 192, 0.2);
      }

      .qa-badge--edited {
        background: var(--app-warn-bg);
        color: var(--app-warn);
        border: 1px solid rgba(230, 81, 0, 0.2);
      }

      .qa-card__actions {
        display: flex;
        gap: 0.4rem;
        align-items: center;
      }

      /* ── Section label ──────────────────────────────────────────────── */
      .qa-section-label {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        font-size: var(--app-font-size-xs);
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--app-text-muted);
        margin-bottom: 0.5rem;
      }

      .qa-section-label .pi {
        font-size: 0.65rem;
      }

      .qa-section-label--ai {
        color: var(--app-primary);
      }

      /* ── Question block ─────────────────────────────────────────────── */
      .qa-card__question-block,
      .qa-card__answer-block {
        display: flex;
        flex-direction: column;
      }

      .qa-card__question-text {
        font-size: var(--app-font-size-md);
        color: var(--app-text-primary);
        font-weight: 600;
        line-height: 1.55;
        margin: 0;
      }

      /* ── Thin divider ───────────────────────────────────────────────── */
      .qa-card__divider {
        height: 1px;
        background: var(--app-border);
        margin: 1rem 0;
      }

      /* ── Answer block ───────────────────────────────────────────────── */
      .qa-card__answer-block--ai {
        background: var(--app-primary-light);
        border-radius: var(--app-radius-sm);
        padding: 0.75rem 1rem;
        border: 1px solid rgba(21, 101, 192, 0.15);
        margin: -0.25rem;
      }

      .qa-answer-skeleton {
        padding: 0.5rem 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .qa-answer-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
      }

      /* ── Textarea — matches input style used in the app ─────────────── */
      :host ::ng-deep .qa-answer-textarea {
        font-family: var(--app-font) !important;
        font-size: var(--app-font-size-base) !important;
        color: var(--app-text-primary) !important;
        line-height: 1.6 !important;
        border: 1px solid var(--app-border) !important;
        border-radius: var(--app-radius-sm) !important;
        background: var(--app-bg) !important;
        resize: vertical;
        transition:
          border-color 0.15s,
          box-shadow 0.15s !important;
        width: 100% !important;
        padding: 0.6rem 0.85rem !important;
        box-sizing: border-box !important;
      }

      :host ::ng-deep .qa-answer-textarea:focus {
        border-color: var(--app-primary) !important;
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.1) !important;
        outline: none !important;
      }

      :host ::ng-deep .qa-answer-textarea--ai {
        border-color: rgba(21, 101, 192, 0.2) !important;
        background: var(--app-surface) !important;
      }

      .qa-answer-footer {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        font-size: var(--app-font-size-xs);
        color: var(--app-primary);
        opacity: 0.8;
      }

      .qa-answer-footer .pi {
        font-size: 0.7rem;
      }

      /* ── Empty state ────────────────────────────────────────────────── */
      .qa-empty {
        max-width: 380px;
        margin: 5rem auto;
        text-align: center;
      }

      .qa-empty__icon-wrap {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: var(--app-primary-light);
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.25rem;
      }

      .qa-empty__icon-wrap .pi {
        font-size: 1.75rem;
        color: var(--app-primary);
      }

      .qa-empty__title {
        font-size: var(--app-font-size-lg);
        font-weight: 700;
        color: var(--app-text-primary);
        margin: 0 0 0.5rem;
      }

      .qa-empty__desc {
        font-size: var(--app-font-size-sm);
        color: var(--app-text-muted);
        margin: 0;
        line-height: 1.6;
      }

      .qa-empty__desc code {
        background: var(--app-bg);
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius-sm);
        padding: 0.1rem 0.35rem;
        font-size: 0.8em;
        color: var(--app-primary);
      }

      /* ── PrimeNG overrides ──────────────────────────────────────────── */

      /* Toast — match app style */
      :host ::ng-deep .p-toast .p-toast-message {
        font-family: var(--app-font) !important;
        font-size: var(--app-font-size-sm) !important;
        border-radius: var(--app-radius) !important;
        box-shadow: var(--app-shadow-hover) !important;
      }

      /* Skeleton shimmer */
      :host ::ng-deep .app-skeleton {
        background: linear-gradient(
          90deg,
          var(--app-border) 25%,
          #eef1f5 50%,
          var(--app-border) 75%
        ) !important;
        background-size: 200% 100% !important;
        animation: qa-shimmer 1.4s infinite !important;
      }

      @keyframes qa-shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }

      /* ConfirmDialog */
      :host ::ng-deep .p-confirmdialog {
        font-family: var(--app-font) !important;
        border-radius: var(--app-radius) !important;
      }

      :host ::ng-deep .p-confirmdialog .p-dialog-header {
        font-size: var(--app-font-size-md) !important;
        font-weight: 700 !important;
        color: var(--app-text-primary) !important;
        padding: 1rem 1.5rem !important;
        border-bottom: 1px solid var(--app-border) !important;
      }

      :host ::ng-deep .p-confirmdialog .p-dialog-content {
        font-size: var(--app-font-size-base) !important;
        color: var(--app-text-secondary) !important;
        padding: 1.25rem 1.5rem !important;
      }

      :host ::ng-deep .p-confirmdialog .p-dialog-footer {
        padding: 0.75rem 1.5rem !important;
        border-top: 1px solid var(--app-border) !important;
      }

      :host ::ng-deep .qa-answer-textarea--editable {
        border-color: var(--app-primary) !important;
        background: var(--app-surface) !important;
        cursor: text !important;
      }

      :host ::ng-deep .qa-answer-textarea--editable:focus {
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
      }

      .qa-editable-hint {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        font-size: var(--app-font-size-xs);
        color: var(--app-primary);
        font-weight: 500;
        margin-top: 0.25rem;
        opacity: 0.75;
      }

      .qa-editable-hint .pi {
        font-size: 0.65rem;
      }

      /* ── Responsive ─────────────────────────────────────────────────── */
      @media (max-width: 768px) {
        .qa-list,
        .qa-header__inner {
          padding: 0 1rem;
        }

        .qa-card {
          padding: 1rem 1.1rem;
        }

        .qa-card__top {
          flex-wrap: wrap;
        }

        .qa-card__header-content {
          flex-direction: column;
          align-items: flex-start;
        }

        .qa-card__actions {
          width: 100%;
        }

        :host ::ng-deep .qa-btn--primary.qa-btn--sm {
          flex: 1;
          justify-content: center;
        }

        .qa-header__title {
          font-size: var(--app-font-size-lg);
        }

        .qa-header__inner {
          flex-direction: column;
          align-items: flex-start;
        }

        .qa-header__actions {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `,
  ],
})
export class DocEditorViewerComponent implements OnInit, OnDestroy, OnChanges {
  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly qaService = inject(QADataService);
  private readonly msgService = inject(AppMessageService);
  private readonly confirmSvc = inject(AppConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly rfpService = inject(RfpService);
  private readonly destroy$ = new Subject<void>();
  private readonly NO_ANSWER_PATTERNS = [
    '[no answer available]',
    'no answer available',
    'no answer',
  ];

  // ── Inputs ──────────────────────────────────────────────────────────────
  @Input() qaInput: QAInputItem[] = [];
  @Input() docsResponse: RfpDocument[] = [];
  @Input() documentId!: number;
  @Input() documentStatus!: string;
  @Input() module!: string;
  @Input() documentTitle = '';
  @Input() docId!: number;
  @Input() clientId!: number;
  @Input() kbDocIds!: string;

  // ── Outputs ─────────────────────────────────────────────────────────────
  @Output() save = new EventEmitter<QASavePayload>();
  @Output() submitDoc = new EventEmitter<QASavePayload>();

  // ── State signals ────────────────────────────────────────────────────────
  readonly qaPairs = signal<QAPair[]>([]);
  readonly Response = signal<QAPair[]>([]);

  isSubmitted = signal<boolean>(false);

  // ── Computed ─────────────────────────────────────────────────────────────
  readonly answeredByAI = computed(
    () => this.qaPairs().filter((p) => p.isAiAnswerActive).length,
  );
  readonly dirtyCount = computed(
    () => this.qaPairs().filter((p) => p.isDirty).length,
  );

  // ─────────────────────────────────────────────────────────────────────────
  // In child component
  readonly submitSuccess = input<boolean>(false);
  savedTrigger = input<number>(0);

  constructor() {
    let previousSavedTrigger = 0;

    effect(
      () => {
        const currentTrigger = this.savedTrigger(); // ← read into variable

        if (this.submitSuccess()) {
          this.isSubmitted.set(true);
          this.cdr.markForCheck();
          // nosonar
          // this.msgService.add({
          //   severity: 'success',
          //   summary: 'Submitted',
          //   detail: 'Document Submitted Successfully.',
          //   life: 4000,
          // });
        }

        // ← Only fire if trigger actually incremented, not on initial/reset
        if (
          currentTrigger > 0 &&
          currentTrigger !== previousSavedTrigger &&
          !this.submitSuccess()
        ) {
          previousSavedTrigger = currentTrigger;
        }

        // ← Reset tracking when trigger resets to 0
        if (currentTrigger === 0) {
          previousSavedTrigger = 0;
        }
      },
      { allowSignalWrites: true },
    );
  }
  ngOnInit(): void {
    this.buildPairsFromInput(this.qaInput);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['qaInput'] && !changes['qaInput'].firstChange) {
      this.buildPairsFromInput(changes['qaInput'].currentValue ?? []);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─────────────────────────────────────────────────────────────────────────

  private buildPairsFromInput(items: QAInputItem[]): void {
    const pairs: QAPair[] = (items ?? []).map((item, index) => ({
      id: toId(index),
      questionNumber: index + 1,
      question: item.question,
      answer: item.answer,
      originalAnswer: item.answer,
      aiExtractedAnswer: undefined,
      isLoadingAiExtract: false,
      isAiAnswerActive: false,
      isDirty: false,
      confidence: undefined,
    }));
    this.qaPairs.set(pairs);
    this.cdr.markForCheck();
  }

  onAnswerEdit(pair: QAPair): void {
    const changed = pair.answer !== pair.originalAnswer;
    // Also clear AI active state if user manually edits an AI answer
    const patch: Partial<QAPair> = { isDirty: changed };
    if (pair.isAiAnswerActive && changed) {
      patch.isAiAnswerActive = false; // ← user overrode AI answer, remove badge
    }
    this.updatePair(pair.id, patch);
  }

  triggerAiExtract(pair: QAPair): void {
    this.updatePair(pair.id, { isLoadingAiExtract: true });

    let payload: any = { questions: [pair.question] };
    const id = this.documentId;
    let result;
    if (typeof id === 'number') {
      result = this.getDocumentByDetailsId(id);
    }
    if (result) {
      Object.assign(payload, result);
    }

    if (this.module === 'rfp') {
      this.rfpService
        .getexcelAnswerFromAi(payload)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.updatePair(pair.id, { isLoadingAiExtract: false });
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (res: AiExtractResponse) => {
            const extracted = res.result?.[0]?.answer;

            if (!extracted) {
              this.msgService.add({
                severity: 'warn',
                summary: 'No Answer Returned',
                detail: `The AI could not find an answer for Q${pair.questionNumber}.`,
              });
              return;
            }

            this.updatePair(pair.id, {
              answer: extracted,
              aiExtractedAnswer: extracted,
              isAiAnswerActive: true,
              isDirty: true,
              confidence: undefined,
            });

            this.msgService.add({
              severity: 'success',
              summary: 'AI Extraction Complete',
              detail: `Q${pair.questionNumber} answer updated.`,
            });
          },
          error: () => {
            this.msgService.add({
              severity: 'error',
              summary: 'Extraction Failed',
              detail: `Could not extract answer for Q${pair.questionNumber}. Please try again.`,
            });
          },
        });
    } else {
      return;
    }
  }

  getDocumentByDetailsId(id: string | number): {
    doc_id: number;
    client_id: number;
    kb_doc_ids: string[] | null | string;
  } | null {
    if (!this.docsResponse || !Array.isArray(this.docsResponse)) {
      return null;
    }
    const doc = this.docsResponse.find((item) => item.id === Number(id));
    if (!doc) return null;
    return {
      doc_id: doc.id,
      client_id: doc.client_id,
      kb_doc_ids:
        doc?.metadata?.rfp_type_id?.toLowerCase() === 'all'
          ? 'all'
          : (doc.kb_doc_ids ?? null),
    };
  }

  resetToOriginal(pair: QAPair): void {
    this.updatePair(pair.id, {
      answer: pair.originalAnswer,
      isAiAnswerActive: false,
      isDirty: false,
      confidence: undefined,
      aiExtractedAnswer: undefined,
    });
    this.msgService.add({
      severity: 'info',
      summary: 'Restored',
      detail: `Q${pair.questionNumber} answer restored to original.`,
    });
  }

  // ── Header actions ────────────────────────────────────────────────────────

  onSave(): void {
    this.save.emit(this.buildPayload());
  }

  onSubmit(): void {
    this.confirmSvc.confirm({
      header: 'Submit Answers',
      message: `You are about to submit ${this.qaPairs().length} answer(s).`,
      // icon:        'pi pi-send',
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-primary',
      accept: () => this.submitDoc.emit(this.buildPayload()),
    });
  }

  /**
   * Call from parent after a successful Save HTTP response.
   * Snapshots current answers as new originals and clears all dirty flags.
   */

  markSubmitted(): void {
    this.isSubmitted.set(true);
    this.cdr.markForCheck();
  }

  // ─────────────────────────────────────────────────────────────────────────

  trackById(_: number, pair: QAPair): string {
    return pair.id;
  }

  private updatePair(id: string, patch: Partial<QAPair>): void {
    this.qaPairs.update((pairs) =>
      pairs.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  }

  private buildPayload(): QASavePayload {
    const docDetails = this.getDocumentByDetailsId(this.documentId);
    return {
      client_id: docDetails?.client_id ?? this.clientId,
      doc_id: docDetails?.doc_id ?? this.docId,
      data: this.qaPairs()
        .filter((p) => p.isDirty)
        .map((p) => ({
          question_no: p.questionNumber,
          question: p.question,
          answer: p.answer,
        })),
    };
  }

  get isReadOnly(): boolean {
    return (
      this.documentStatus?.toLowerCase() !== 'created' || this.isSubmitted()
    );
  }

  // isAnswerEditable(pair: QAPair): boolean {
  //   if (this.documentStatus?.toLowerCase() !== 'created' || this.isSubmitted()) {
  //     return false;
  //   }
  //   const normalized = pair.answer?.trim().toLowerCase() ?? '';
  //   return this.NO_ANSWER_PATTERNS.some(p => normalized === p);
  // }

  isAnswerEditable(pair: QAPair): boolean {
    if (
      this.documentStatus?.toLowerCase() !== 'created' ||
      this.isSubmitted()
    ) {
      return false;
    }
    const normalized = pair.originalAnswer?.trim().toLowerCase() ?? '';
    return this.NO_ANSWER_PATTERNS.some((p) => normalized === p);
  }

  //  — all pairs, same structure as AI extract uses
  // private buildPayload(): QASavePayload {
  //   const docDetails = this.getDocumentByDetailsId(this.documentId);
  //   return {
  //     client_id: docDetails?.client_id ?? this.clientId,
  //     doc_id: docDetails?.doc_id ?? this.docId,
  //     data: this.qaPairs().map((p) => ({   // ← all pairs, no filter
  //       question_no: p.questionNumber,
  //       answer: p.answer,
  //     })),
  //   };
  // }
}
